import type { ReactElement, ReactNode } from "react"
import styled from "styled-components"
import { ENV, missingEnvironmentKeys } from "~/ENV"
import { useAuth } from "~/features/auth/useAuth"
import { Button, StateMessage } from "~/shared/ui"

const Prompt = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: ${({ theme }) => theme.spacing.md};
  padding: ${({ theme }) => theme.spacing.xl};
  border-radius: ${({ theme }) => theme.radius.lg};
  background-color: ${({ theme }) => theme.color.surface};
`

export const RequireAuth = ({ children }: { readonly children: ReactNode }): ReactElement => {
  const { status, signIn } = useAuth()
  const missing = missingEnvironmentKeys()

  if (missing.length > 0) {
    return (
      <StateMessage tone="error" qaHook="app-config-error">
        {`Missing configuration: ${missing.join(", ")}. Copy example.env to .env and fill it in.`}
      </StateMessage>
    )
  }

  if (status === "initializing") {
    return <StateMessage qaHook="app-auth-initializing">Checking your session…</StateMessage>
  }

  if (status === "signedOut") {
    return (
      <Prompt data-qa-hook="app-sign-in-prompt">
        <p>Sign in with your Lifeway account to see your Digital Library.</p>
        <Button type="button" data-qa-hook="app-sign-in-button" onClick={() => void signIn()}>
          {`Sign in at ${new URL(ENV.LIFEWAY_ACCOUNT_URL).host}`}
        </Button>
      </Prompt>
    )
  }

  return <>{children}</>
}
